import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Sidebar from '../../../Components/Sidebar/Sidebar';
import axios from 'axios';
import '../../styles/inventario.css';
import './HistoricoHardware.css';
import HistoricoTimeline from '../../components/HistoricoTimeline/HistoricoTimeline';
import verificandoToken from '../../../../FuncoesGerais/VerificarToken';
import verificandoApp from '../../../../FuncoesGerais/verificaApp';
import { ensureProtocol } from '../../../../config/backend';

const DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 15;
const ENRICH_USER_LIMIT = 8;

function HistoricoHardware(props) {
    const history = useHistory();
    const [codHar, setCodHar] = useState(props.match?.params?.id || '');
    const [data, setData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');
    const debounceRef = useRef(null);
    const dropdownRef = useRef(null);

    const verificar = useCallback(async () => {
        const resultado = await verificandoApp('ATENDENTE');
        if (!resultado) history.push('/');
    }, [history]);

    const fetchHistory = useCallback(async (cod) => {
        if (!cod || !cod.toString().trim()) {
            setData([]);
            return;
        }
        setLoadingHistory(true);
        verificandoToken();
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/hishardware/hardware', {
                headers: { Authorization: `${token}` },
                params: { codHar: cod }
            });
            const raw = Array.isArray(response.data) ? response.data : [];
            setData(raw.map((r) => ({
                codhar: r.codhar ?? r.CODHAR,
                deshis: r.deshis ?? r.DESHIS ?? '',
                seqhis: r.seqhis ?? r.SEQHIS,
                dathis: r.dathis ?? r.DATHIS ?? ''
            })));
        } catch (err) {
            console.error(err);
            setData([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    const searchHardware = useCallback(async (query) => {
        const q = (query || '').toString().trim();
        if (q.length === 0) {
            setSuggestions([]);
            return;
        }
        setLoadingSearch(true);
        verificandoToken();
        const token = localStorage.getItem('token');
        const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2'));
        try {
            const response = await axios.get(baseUrl + '/hardware', {
                headers: { Authorization: `${token}` },
                params: { descr: q || ' ' }
            });
            const raw = Array.isArray(response.data) ? response.data : [];
            let list = raw.slice(0, MAX_SUGGESTIONS).map((h) => ({
                codhar: h.codhar ?? h.CODHAR,
                codeqp: h.codeqp ?? h.CODEQP,
                sereqp: (h.sereqp ?? h.SEREQP ?? '').toString(),
                pateqp: (h.pateqp ?? h.PATEQP ?? '').toString(),
                deseqp: (h.deseqp ?? h.DESEQP ?? '').toString()
            }));
            if (list.length > 0) {
                const toEnrich = list.slice(0, ENRICH_USER_LIMIT);
                const userResults = await Promise.allSettled(
                    toEnrich.map((item) =>
                        axios.get(baseUrl + '/vinculacao/hardware', {
                            headers: { Authorization: `${token}` },
                            params: { codHar: item.codhar }
                        }).then((res) => ({ codhar: item.codhar, nomUsu: (res.data && res.data[0]) ? (res.data[0].USRNOMCPL ?? res.data[0].usrnomcpl ?? '').toString() : '' }))
                    )
                );
                const userMap = {};
                userResults.forEach((r, i) => {
                    if (r.status === 'fulfilled' && r.value && r.value.nomUsu) userMap[r.value.codhar] = r.value.nomUsu;
                });
                list = list.map((item) => ({ ...item, nomUsu: userMap[item.codhar] || '' }));
            }
            setSuggestions(list);
        } catch (err) {
            console.error(err);
            setSuggestions([]);
        } finally {
            setLoadingSearch(false);
        }
    }, []);

    useEffect(() => {
        verificar();
    }, [verificar]);

    useEffect(() => {
        if (props.match?.params?.id) {
            setCodHar(props.match.params.id);
            fetchHistory(props.match.params.id);
        }
    }, [props.match?.params?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (searchQuery.trim().length === 0) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        debounceRef.current = setTimeout(() => {
            searchHardware(searchQuery);
            setShowDropdown(true);
        }, DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchQuery, searchHardware]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getSuggestionLabel = (item) => {
        const name = (item.deseqp || item.sereqp || `#${item.codhar}`).toString().trim().split('\n')[0].substring(0, 50);
        const pat = item.pateqp ? `Pat. ${item.pateqp}` : '';
        const user = item.nomUsu ? item.nomUsu : '';
        return [name, pat, user].filter(Boolean).join(' — ');
    };

    const handleSelectSuggestion = (item) => {
        setCodHar(item.codhar);
        setSelectedLabel(getSuggestionLabel(item));
        setSearchQuery('');
        setSuggestions([]);
        setShowDropdown(false);
        fetchHistory(item.codhar);
    };

    const handleHistoryDeleted = useCallback(() => {
        fetchHistory(codHar);
    }, [codHar, fetchHistory]);

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="container-fluid titulo inventario-page historico-hardware-page">
                    <h1 className="historico-hardware-page__title">Histórico de hardwares</h1>
                    <p className="historico-hardware-page__subtitle">Busque por nome, patrimônio, série ou usuário atribuído e selecione um ativo para ver o histórico.</p>

                    <div className="historico-hardware-search" ref={dropdownRef}>
                        <div className="historico-hardware-search__input-wrap">
                            <i className="fas fa-search historico-hardware-search__icon" aria-hidden="true" />
                            <input
                                type="text"
                                className="form-control historico-hardware-search__input"
                                placeholder="Buscar por nome, patrimônio, série ou usuário..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                                aria-autocomplete="list"
                                aria-controls="historico-suggestions"
                                id="historico-search"
                            />
                            {loadingSearch && (
                                <span className="historico-hardware-search__spinner" aria-hidden="true">
                                    <i className="fas fa-spinner fa-spin" />
                                </span>
                            )}
                        </div>
                        {showDropdown && suggestions.length > 0 && (
                            <ul id="historico-suggestions" className="historico-hardware-search__dropdown" role="listbox">
                                {suggestions.map((item) => (
                                    <li
                                        key={item.codhar}
                                        role="option"
                                        aria-selected="false"
                                        className="historico-hardware-search__suggestion"
                                        onClick={() => handleSelectSuggestion(item)}
                                    >
                                        <span className="historico-hardware-search__suggestion-label">{getSuggestionLabel(item)}</span>
                                        <span className="historico-hardware-search__suggestion-code">#{item.codhar}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {(codHar && selectedLabel) && (
                        <div className="historico-hardware-selected">
                            <span className="historico-hardware-selected__label">Ativo selecionado:</span>
                            <strong className="historico-hardware-selected__value">{selectedLabel || `#${codHar}`}</strong>
                        </div>
                    )}

                    {codHar && !selectedLabel && searchQuery.length === 0 && (
                        <div className="historico-hardware-selected">
                            <span className="historico-hardware-selected__label">Ativo:</span>
                            <strong className="historico-hardware-selected__value">#{codHar}</strong>
                        </div>
                    )}

                    <section className="historico-hardware-timeline-section">
                        <h2 className="historico-hardware-timeline-section__title">Linha do tempo</h2>
                        {loadingHistory ? (
                            <div className="historico-hardware-loading"><i className="fas fa-spinner fa-spin" /> Carregando histórico...</div>
                        ) : (
                            <HistoricoTimeline arrayHistoricoHardware={data} onDeleted={handleHistoryDeleted} />
                        )}
                    </section>

                    <div className="historico-hardware-footer">
                        <Link to="/listaHardware" className="btn btn-secondary">Voltar ao Inventário</Link>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default HistoricoHardware;
